import { RecapImagesModel, RecapImageUpload } from '../../models/RecapImagesModel'
import { useTaskProfiles } from '../../store/useTaskProfiles'
import { SyncData, ToReset } from '../../types/sync'
import { StoreRecapImagesModel, StoreRecapImageUpload } from '../../models/StoreRecapImagesModel'
import { isNullish, logInfo, stripDataTag } from '../../utils'
import { RecapDefinitionsModel } from '../../models/RecapDefinitionsModel'
import { Recap } from '../../types/recap'
import { addBreadcrumb } from '@sentry/vue'

export function recapImagesSync(setSyncProgress: (message: string | null, percentage: number) => void) {
  async function setTaskImages(syncData: SyncData): Promise<ToReset[]> {
    // Add a breadcrumb for debugging missing images (NPUB-866)
    const { getRecapImagesForRecap, getRecapImagesForRecapField, queryAllRecapImages } =
      RecapImagesModel()
    addBreadcrumb({
      level: 'debug',
      category: 'RecapImageDBData',
      message: 'Current Recap Image Database Rows',
      data: {
        rows: await queryAllRecapImages(),
      },
    })

    // Get all the recap images
    const toReset: ToReset[] = []
    if (syncData.recap_responses && syncData.recap_responses.length) {
      const { getTaskByTaskId } = useTaskProfiles()
      for (const response of syncData.recap_responses) {
        const task = getTaskByTaskId(response.task_id || 0)
        if (task) {
          const { getRecapDefinition } = RecapDefinitionsModel()
          const images =
            (await getRecapImagesForRecap(response.events_recaps_id, response.task_id!)) || []
          // NPUB-866
          // Do an additional check to see if every field that required an image has one
          // If one (or more) is missing, log an error
          const recapResult = await getRecapDefinition(response.events_recaps_id)
          if (recapResult && recapResult.length) {
            const requiredImageFields = []
            const definitionParsed = JSON.parse(recapResult[0].recap_definition)
            if (definitionParsed) {
              for (const page of (definitionParsed as Recap).pages) {
                for (const form of page.forms || []) {
                  for (const field of form.fields || []) {
                    const isRequired = field.validations.some(
                      (validation) => validation.validation_type === 'responseRequired'
                    )
                    const isImage =
                      field.field.defined_as === 'FIELD_IMAGE_UPLOAD' ||
                      field.field.defined_as === 'FIELD_IMAGE_UPLOAD_MULTIPLE'
                    if (isRequired && isImage) {
                      requiredImageFields.push(String(field.id))
                    }
                  }
                }
              }
              let lastFormId = '0'
              let repeatingIndex = 0
              for (const form of response.form_responses) {
                if (lastFormId !== form.id) {
                  lastFormId = String(form.events_custom_forms_id)
                  repeatingIndex = 0
                } else {
                  repeatingIndex++
                }
                for (const fieldResponse of form.field_responses) {
                  const isRequiredImage = requiredImageFields.includes(
                    String(fieldResponse.events_custom_instances_id)
                  )
                  if (isRequiredImage) {
                    if ( isNullish(response.events_recaps_id) ||
                       isNullish(response.task_id!) ||
                       isNullish(fieldResponse.events_custom_instances_id) ||
                       isNullish(repeatingIndex)) {
                        logInfo(
                          'error',
                          'getRecapImagesForRecapField missing required fields',
                          `Inputs missing for getRecapImagesForRecapField: ${response.events_recaps_id} ${response.task_id!} ${fieldResponse.events_custom_instances_id} ${repeatingIndex}`,
                          {event_recaps_id: response.events_recaps_id, task_id: response.task_id, events_custom_instances_id: fieldResponse.events_custom_instances_id, repeating_index: repeatingIndex}
                        )
                        continue
                      }
                    const imagesForField = await getRecapImagesForRecapField(
                      response.events_recaps_id,
                      response.task_id!,
                      fieldResponse.events_custom_instances_id,
                      repeatingIndex
                    )
                    if (!imagesForField || !imagesForField.length) {
                      logInfo(
                        'error',
                        'ImageMissingSync',
                        `Required image missing for task ${response.task_id}`,
                        { response }
                      )
                    }
                    const imageIds = imagesForField.map((img) => String(img.id))
                    const foundImage = images.find((img) => imageIds.includes(String(img.id)))
                    if (!foundImage) {
                      logInfo(
                        'error',
                        'ImageMissingWarn',
                        `Required image for task ${response.task_id} not found for response, but was found for field. Adding.`,
                        { response }
                      )
                      images.push(...imagesForField)
                    }
                  }
                }
              }
            }
          }
          // Remove any data tags from the images and add them to the response
          if (images && images.length) {
            response.images = []
            const uploads = images as RecapImageUpload[]
            uploads.forEach((img, idx) => {
              setSyncProgress(`Task images (process): ( ${idx}/${images.length} )`, 22 + ((idx / images.length) * 4))
              const imageBase64 = stripDataTag(img.data)
              response.images?.push({
                data: imageBase64,
                metadata: JSON.parse(img.metadata || '{}'),
              })
            })
          }
        } else {
          // Somehow, we have a recap response without a corresponding task on the device
          // This could happen if we have old responses for completed tasks sticking around for some reason
          // Otherwise, this is a pretty weird case, but either way we don't want to sync the recap
          logInfo(
            'debug',
            'Recap Response: no task',
            'No task exists for the given recap response',
            { response, allResponses: syncData.recap_responses }
          )
          toReset.push({ task_id: undefined, response })
        }
      }
    }
    return toReset
  }

  async function setStoreImages(syncData: SyncData): Promise<void> {
    // Add a breadcrumb for debugging
    const { getStoreRecapImagesForRecap, queryAllStoreRecapImages } = StoreRecapImagesModel()
    addBreadcrumb({
      level: 'debug',
      category: 'StoreRecapImageDBData',
      message: 'Current Store Recap Image Database Rows',
      data: {
        rows: await queryAllStoreRecapImages(),
      },
    })

    // Get all the store recap images
    if (syncData.store_recap_responses && syncData.store_recap_responses.length) {
      for (const response of syncData.store_recap_responses) {
        const images = await getStoreRecapImagesForRecap(
          response.events_recaps_id,
          response.store_id!
        ).catch((err) =>
          console.error(
            `Error fetching images for recap ${response.events_recaps_id} and store ${response.store_id}: `,
            err
          )
        )
        if (images && images.length) {
          response.images = []
          const uploads = images as StoreRecapImageUpload[]
          uploads.forEach((img, idx) => {
            setSyncProgress(`Store images (process): ( ${idx}/${images.length} )`, 24 + ((idx / images.length) * 4))
            const imageBase64 = stripDataTag(img.data)
            response.images?.push({
              data: imageBase64,
              metadata: JSON.parse(img.metadata || '{}'),
            })
          })
        }
      }
    }
  }

  return {
    setTaskImages,
    setStoreImages,
  }
}
